import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Filter, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { projectsApi } from '../lib/api';
import { formatCurrency, PROJECT_STATUSES } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { ProjectFormDialog } from '../components/projects/ProjectFormDialog';
import { toast } from 'sonner';

export const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState('status');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await projectsApi.getAll();
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectCreated = (newProject) => {
    if (newProject.openJvForm !== undefined) {
      setShowForm(false);
      if (newProject.openJvForm) {
        navigate(`/projects/${newProject.id}?openJv=true`);
        toast.success('Project created! Now add the JV agreement.');
      } else {
        loadProjects();
        toast.success('Project created');
      }
      return;
    }
    setProjects(prev => [...prev, newProject]);
    setShowForm(false);
    toast.success(`Project "${newProject.name}" created`);
  };

  const STATUS_ORDER = {
    'Under_Contract_Sell': 0, 'Listed': 1, 'Subdividing': 2, 'Owned': 3,
    'Under_Contract': 4, 'In_Due_Diligence': 5, 'In_Closing': 6, 'LOI': 7,
    'Lead': 8, 'Sold': 9, 'Sold_Cash': 10, 'Sold_Note': 11, 'Note_Held': 12, 'Dead': 13,
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ columnKey }) => {
    if (sortKey !== columnKey) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3 h-3 text-foreground" />
      : <ArrowDown className="w-3 h-3 text-foreground" />;
  };

  const sortedProjects = useMemo(() => {
    const filtered = projects.filter(project => {
      const matchesSearch = project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.county && project.county.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (project.state && project.state.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'status':
          return dir * ((STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99));
        case 'location':
          return dir * (`${a.state || ''}${a.county || ''}`).localeCompare(`${b.state || ''}${b.county || ''}`);
        case 'purchase_price':
          return dir * ((a.purchase_price || 0) - (b.purchase_price || 0));
        case 'sale_price':
          return dir * ((a.sale_price || 0) - (b.sale_price || 0));
        case 'strategy':
          return dir * (a.strategy || '').localeCompare(b.strategy || '');
        case 'acres':
          return dir * ((a.acres || 0) - (b.acres || 0));
        case 'estimated_close_date':
          return dir * ((a.estimated_close_date || '9999').localeCompare(b.estimated_close_date || '9999'));
        default:
          return 0;
      }
    });
  }, [projects, searchQuery, statusFilter, sortKey, sortDir]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="projects-loading">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const columns = [
    { key: 'name', label: 'Project', align: 'left' },
    { key: 'status', label: 'Status', align: 'left' },
    { key: 'location', label: 'Location', align: 'left' },
    { key: 'purchase_price', label: 'Purchase', align: 'right' },
    { key: 'sale_price', label: 'Sale', align: 'right' },
    { key: 'estimated_close_date', label: 'Est. Close', align: 'center' },
    { key: 'strategy', label: 'Strategy', align: 'center' },
    { key: 'acres', label: 'Acres', align: 'right' },
  ];

  return (
    <div className="space-y-6" data-testid="projects-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Projects</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {sortedProjects.length} of {projects.length} projects
          </p>
        </div>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-forest-500 hover:bg-forest-600 text-white rounded-sm uppercase text-xs font-semibold tracking-wide w-full sm:w-auto"
          data-testid="add-project-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-sm"
            data-testid="search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 rounded-sm" data-testid="status-filter">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {PROJECT_STATUSES.map(status => (
              <SelectItem key={status} value={status}>{status.replace(/_/g, ' ')}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Projects Table */}
      {sortedProjects.length > 0 ? (
        <div className="border rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="projects-table">
              <thead>
                <tr className="border-b bg-muted/30">
                  {columns.map(col => (
                    <th
                      key={col.key}
                      className={`py-2.5 px-4 text-xs uppercase tracking-wide text-muted-foreground font-semibold cursor-pointer hover:text-foreground transition-colors select-none ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'}`}
                      onClick={() => handleSort(col.key)}
                      data-testid={`sort-${col.key}`}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {col.label}
                        <SortIcon columnKey={col.key} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedProjects.map((project) => (
                  <tr
                    key={project.id}
                    className="border-b last:border-0 hover:bg-muted/20 cursor-pointer transition-colors"
                    onClick={() => navigate(`/projects/${project.id}`)}
                    data-testid={`project-row-${project.id}`}
                  >
                    <td className="py-3 px-4">
                      <p className="font-medium text-sm">{project.name}</p>
                    </td>
                    <td className="py-3 px-4">
                      <StatusBadge status={project.status} />
                    </td>
                    <td className="py-3 px-4 text-sm text-muted-foreground">
                      {project.county && project.state
                        ? `${project.county}, ${project.state}`
                        : project.state || '-'}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm">
                      {formatCurrency(project.purchase_price)}
                    </td>
                    <td className="py-3 px-4 text-right font-mono text-sm">
                      {project.sale_price ? (
                        <span className="text-emerald-600">{formatCurrency(project.sale_price)}</span>
                      ) : project.estimated_sale_price ? (
                        <span className="text-muted-foreground" title="Estimated">{formatCurrency(project.estimated_sale_price)}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center text-sm">
                      {project.estimated_close_date ? (
                        <span className="font-mono text-xs text-muted-foreground">{new Date(project.estimated_close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</span>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge
                        variant="outline"
                        className={`text-[10px] rounded-sm ${project.strategy === 'Flip' ? 'badge-flip' : 'badge-subdivide'}`}
                      >
                        {project.strategy}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-right text-sm text-muted-foreground font-mono">
                      {project.acres ? Number(project.acres).toFixed(2) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <Card className="rounded-sm border">
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">
              {searchQuery || statusFilter !== 'all'
                ? 'No projects match your filters.'
                : 'No projects yet. Create your first project to get started.'}
            </p>
          </CardContent>
        </Card>
      )}

      <ProjectFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        onSuccess={handleProjectCreated}
      />
    </div>
  );
};
